import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UNIVERSE, STORY } from "@/lib/universe";
import { ArrowLeft, Heart, Radio, Pause, Play, RotateCcw, Trophy, ShoppingBag, Share2 } from "lucide-react";
import ShopModal from "@/components/ShopModal";
import ShareRankCard from "@/components/ShareRankCard";
import WalletBadge from "@/components/WalletBadge";
import TournamentResultModal from "@/components/TournamentResultModal";
import RequiredPassportModal from "@/components/RequiredPassportModal";
import MobileControls from "@/components/MobileControls";
import { useActiveTournament, TournamentHUD, TournamentPendingHUD, TournamentLiveBoard } from "@/lib/tournament";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { FEATURES } from "@/lib/features";

const API = process.env.REACT_APP_BACKEND_URL;

// Texture pool for asteroids/background (reuses planet textures from universe.js)
const ASTEROID_TEX = [];
const BG_TEX = [];
UNIVERSE.galaxies.forEach((g) => {
  g.systems.forEach((s) => {
    s.planets.forEach((pl) => {
      if (pl.tex) {
        ASTEROID_TEX.push(pl.tex);
        BG_TEX.push(pl.tex); // planets as backgrounds — not blinding suns
      }
    });
  });
});

const loader = new THREE.TextureLoader();
const texCache = new Map();
function loadTex(url) {
  if (!url) return null;
  if (texCache.has(url)) return texCache.get(url);
  const t = loader.load(url);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  texCache.set(url, t);
  return t;
}

// World bounds
const B = { x: 42, z: 22 };

export default function PilotGame() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const mountRef = useRef(null);
  const gameRef = useRef({});
  const [hud, setHud] = useState({ score: 0, lives: 3, level: 1, signals: 0, signalsGoal: 5, state: "menu" });
  const [pilotName, setPilotName] = useState(() => localStorage.getItem("ufo_pilot_name") || "");
  const [leaderboard, setLeaderboard] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ufo_pilot_lb") || "[]"); } catch { return []; }
  });
  const [shopOpen, setShopOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [currentSkin, setCurrentSkin] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [globalLb, setGlobalLb] = useState([]);
  // H-2 anti-cheat: server-issued HMAC token proving this /pilot/score
  // submission is tied to a game that actually started X seconds ago.
  const gameTokenRef = useRef(null);
  const { active: activeTournament, pending: pendingTournament, liveBoard: tournamentLiveBoard, submitScore: submitTournamentScore } = useActiveTournament(user, gameRef);

  const fetchMyStats = useCallback(async () => {
    if (!user) { setMyStats(null); return; }
    try {
      const r = await fetch(`${API}/api/pilot/me/stats`, { credentials: "include" });
      if (r.ok) setMyStats(await r.json());
    } catch (err) {
      console.warn("[pilot] failed to load stats:", err);
    }
  }, [user]);

  useEffect(() => { fetchMyStats(); }, [fetchMyStats]);

  // If the user has a passport, its handle is the LOCKED pilot name — sync it into pilotName
  // so the game rendering + score submission use the canonical callsign.
  useEffect(() => {
    const handle = myStats?.passport_handle;
    if (handle && handle !== pilotName) {
      setPilotName(handle);
      try { localStorage.setItem("ufo_pilot_name", handle); } catch { /* ignore */ }
    }
  }, [myStats?.passport_handle, pilotName]);

  const passportLocked = Boolean(myStats?.passport_handle);

  const fetchGlobalLb = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/pilot/leaderboard`);
      if (r.ok) {
        const d = await r.json();
        setGlobalLb(d.top || []);
      }
    } catch (err) {
      console.warn("[pilot] failed to load leaderboard:", err);
    }
  }, []);

  useEffect(() => { fetchGlobalLb(); }, [fetchGlobalLb]);

  const startGame = useCallback(() => {
    gameRef.current.reset?.();
    setHud((h) => ({ ...h, state: "playing", score: 0, lives: 3, level: 1, signals: 0, signalsGoal: 5 }));
    // H-2 anti-cheat: fetch a server-signed game_token pinning the start time.
    // On score submit we send this back so the server can verify elapsed wall-clock
    // time is consistent with the reported score (max 80 pts/sec).
    if (user) {
      gameTokenRef.current = null;
      fetch(`${API}/api/pilot/game/start`, {
        method: "POST", credentials: "include",
      })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (d?.game_token) gameTokenRef.current = d.game_token; })
        .catch((err) => { console.warn("[pilot] failed to start game session:", err); });
    }
    // Server-side timing: if this player is in an active tournament, tell the
    // backend the game started so it can compute the run duration itself and
    // reject inflated `duration_seconds` values from a modified client.
    if (activeTournament && user) {
      fetch(`${API}/api/rooms/${activeTournament.room_id}/game/start`, {
        method: "POST", credentials: "include",
      }).catch(() => { /* silent: falls back to client duration */ });
    }
  }, [activeTournament, user]);

  const pause = useCallback(() => {
    setHud((h) => ({ ...h, state: h.state === "playing" ? "paused" : (h.state === "paused" ? "playing" : h.state) }));
  }, []);

  const submitScore = useCallback(async () => {
    if (!pilotName.trim()) return;
    const entry = { name: pilotName.trim().slice(0, 14).toUpperCase(), score: hud.score, level: hud.level, at: Date.now() };
    const lb = [...leaderboard, entry].sort((a, b) => b.score - a.score).slice(0, 10);
    setLeaderboard(lb);
    localStorage.setItem("ufo_pilot_lb", JSON.stringify(lb));
    localStorage.setItem("ufo_pilot_name", pilotName.trim());
    // Also send to backend if authenticated — unlocks progressive shop discounts + global leaderboard
    if (user) {
      try {
        await fetch(`${API}/api/pilot/score`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            score: hud.score,
            level: hud.level,
            name: entry.name,
            game_token: gameTokenRef.current || undefined,
          }),
        });
      } catch (err) {
        console.warn("[pilot] failed to submit score:", err);
      }
    }
    // Also submit to active tournament if any (best-score-only)
    if (activeTournament) {
      // Rough duration estimate: use score-based heuristic (avg 30 pts/sec) since we don't track start time
      const durationSec = Math.max(10, Math.min(3600, Math.round(hud.score / 30)));
      submitTournamentScore({ score: hud.score, level: hud.level, durationSeconds: durationSec });
      toast.success(t("pilot.toast_score_submitted", { score: hud.score.toLocaleString(), room: activeTournament.label }));
    } else if (pendingTournament) {
      const s = Math.max(0, pendingTournament.starts_in_seconds);
      const m = Math.floor(s / 60);
      toast.warning(t("pilot.toast_score_not_counted", { room: pendingTournament.label, minutes: m }), { duration: 6000 });
    }
    // refresh stats + global leaderboard
    fetchMyStats();
    fetchGlobalLb();
    setHud((h) => ({ ...h, state: "menu" }));
  }, [hud.score, hud.level, pilotName, leaderboard, user, activeTournament, pendingTournament, submitTournamentScore, fetchMyStats, fetchGlobalLb]);

  // Init Three.js scene
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    // Transparent scene (no opaque background color) so the nebula <img> behind the canvas
    // shows through cleanly. Ships/geometry render opaque on top → no "ghost/transparent" look.
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(55, mount.clientWidth / mount.clientHeight, 0.1, 500);
    camera.position.set(0, 42, 12);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: false,      // was true — biggest fragment-shader cost on top-down shooter
      alpha: true,
      powerPreference: "high-performance",
    });
    // Render at 65% internal resolution and let CSS upscale to 100% — huge fragment-shader
    // savings on software / integrated GPUs. Barely noticeable visually because the game
    // is top-down and objects are already at moderate detail.
    const RES_SCALE = 0.65;
    renderer.setPixelRatio(1);
    renderer.setSize(Math.floor(mount.clientWidth * RES_SCALE), Math.floor(mount.clientHeight * RES_SCALE), false);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;   // cheaper than ACESFilmic; scene already uses emissive materials
    mount.appendChild(renderer.domElement);

    // Lighting — bright enough to see 3D ship clearly
    scene.add(new THREE.AmbientLight(0xaaccff, 1.8));
    const dir = new THREE.DirectionalLight(0xffffff, 1.8);
    dir.position.set(20, 40, 15);
    scene.add(dir);
    const rim = new THREE.DirectionalLight(0x66aaff, 0.8);
    rim.position.set(-30, 20, -30);
    scene.add(rim);
    const fill = new THREE.DirectionalLight(0xffddaa, 0.5);
    fill.position.set(0, 10, 30);
    scene.add(fill);

    // Background handled by CSS on the wrapper div — full-quality PNG shown as-is.
    // Kept refs as no-op stubs so the animation loop code doesn't need to change.
    const bgSphere = { rotation: { x: 0, y: 0 }, material: { color: new THREE.Color(0xffffff) } };
    const dustSphere = { rotation: { x: 0, y: 0 } };

    // NOTE: Additional Three.js clutter layers (near stars, parallax stars, warp streaks and
    // ground grid) were intentionally removed per user request — the deep-space Milky Way
    // background image behind the canvas is enough atmosphere. Only game elements
    // (ship, enemies, asteroids, comet, boss, bullets, signals) render on the transparent scene.

    // Player group — content is (re)built dynamically by applySkin()
    const playerGroup = new THREE.Group();
    scene.add(playerGroup);
    // engine glow (kept between skins)
    const engineGlow = new THREE.PointLight(0x33e0ff, 3.2, 14);
    engineGlow.position.y = -0.4;
    playerGroup.add(engineGlow);
    // Ambient boost light that follows the player — makes the ship pop
    const playerSpot = new THREE.PointLight(0xffffff, 3.5, 10);
    playerSpot.position.set(0, 3, 0);
    playerGroup.add(playerSpot);

    function buildProceduralUfo(skin) {
      const grp = new THREE.Group();
      const bodyColor = skin?.color ? new THREE.Color(skin.color) : new THREE.Color(0x334466);
      const glowColor = skin?.glow_color ? new THREE.Color(skin.glow_color) : new THREE.Color(0x33e0ff);
      const disc = new THREE.Mesh(
        new THREE.CylinderGeometry(1.1, 1.6, 0.35, 32),
        new THREE.MeshStandardMaterial({ color: bodyColor, metalness: 0.7, roughness: 0.35, emissive: glowColor.clone().multiplyScalar(0.9), emissiveIntensity: 1.4 })
      );
      grp.add(disc);
      const dome = new THREE.Mesh(
        new THREE.SphereGeometry(0.75, 24, 24, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: glowColor, metalness: 0.4, roughness: 0.1, transparent: true, opacity: 0.85, emissive: glowColor, emissiveIntensity: 1.6 })
      );
      dome.position.y = 0.15;
      grp.add(dome);
      engineGlow.color = glowColor;
      engineGlow.intensity = 3.2;
      return grp;
    }

    function clearShipMeshes() {
      const toRemove = [];
      playerGroup.children.forEach((c) => { if (c !== engineGlow && c !== playerSpot) toRemove.push(c); });
      toRemove.forEach((c) => {
        playerGroup.remove(c);
        c.traverse?.((o) => {
          if (o.geometry) o.geometry.dispose();
          if (o.material) {
            const mats = Array.isArray(o.material) ? o.material : [o.material];
            mats.forEach((m) => m.dispose?.());
          }
        });
      });
      // dispose trail if any
      if (gameRef.current._trail) {
        scene.remove(gameRef.current._trail);
        gameRef.current._trail.geometry?.dispose();
        gameRef.current._trail.material?.dispose();
        gameRef.current._trail = null;
      }
    }

    function applyTrailAndParticles(skin) {
      if (skin?.trail) {
        const trailGeo = new THREE.PlaneGeometry(1.2, 4);
        const trailMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(skin.glow_color), transparent: true, opacity: 0.55, side: THREE.DoubleSide, depthWrite: false });
        const trail = new THREE.Mesh(trailGeo, trailMat);
        trail.rotation.x = -Math.PI / 2;
        trail.position.set(0, -0.15, 2.3);
        playerGroup.add(trail);
        gameRef.current._trailMesh = trail;
      } else {
        gameRef.current._trailMesh = null;
      }
      // particles: use Points around the ship
      if (skin?.particles) {
        const pcount = 80;
        const pos = new Float32Array(pcount * 3);
        for (let i = 0; i < pcount; i++) {
          pos[i * 3] = (Math.random() - 0.5) * 4;
          pos[i * 3 + 1] = (Math.random() - 0.5) * 0.6;
          pos[i * 3 + 2] = (Math.random() - 0.5) * 4;
        }
        const pgeo = new THREE.BufferGeometry();
        pgeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
        const pmat = new THREE.PointsMaterial({ color: new THREE.Color(skin.glow_color), size: 0.15, transparent: true, opacity: 0.9, depthWrite: false, sizeAttenuation: true });
        const points = new THREE.Points(pgeo, pmat);
        playerGroup.add(points);
        gameRef.current._particles = points;
      } else {
        gameRef.current._particles = null;
      }
    }

    gameRef.current.applySkin = (skin) => {
      clearShipMeshes();
      if (!skin || skin.model_type === "procedural") {
        const ufo = buildProceduralUfo(skin);
        playerGroup.add(ufo);
        applyTrailAndParticles(skin);
        return;
      }
      // GLB path
      const proc = buildProceduralUfo(skin); // temporary while GLB loads
      playerGroup.add(proc);
      applyTrailAndParticles(skin);
      const gltfLoader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("/draco/");
      gltfLoader.setDRACOLoader(dracoLoader);
      gltfLoader.load(
        skin.model_url,
        (gltf) => {
          const ship = gltf.scene;
          const box = new THREE.Box3().setFromObject(ship);
          const size = new THREE.Vector3(); box.getSize(size);
          const maxDim = Math.max(size.x, size.y, size.z) || 1;
          const s = 5.5 / maxDim;
          ship.scale.setScalar(s);
          const center = new THREE.Vector3(); box.getCenter(center);
          ship.position.sub(center.multiplyScalar(s));
          ship.rotation.y = Math.PI;
          // Only tint the model when the catalog explicitly defines a race color.
          // Race-specific .glb files (with their own materials) leave color/glow_color null,
          // so the artist's original textures show through as-is.
          if (skin.color) {
            const tint = new THREE.Color(skin.color);
            const glow = new THREE.Color(skin.glow_color || skin.color);
            ship.traverse((o) => {
              if (o.isMesh && o.material) {
                const mats = Array.isArray(o.material) ? o.material : [o.material];
                mats.forEach((m) => {
                  if (m.color) m.color.lerp(tint, 0.55);
                  m.emissive = glow.clone().multiplyScalar(0.55);
                  m.emissiveIntensity = 1.0;
                  m.metalness = 0.6;
                  m.roughness = 0.4;
                  m.needsUpdate = true;
                });
              }
            });
            const rimLight = new THREE.PointLight(glow, 2.5, 8);
            rimLight.position.set(0, 0.5, 0);
            ship.add(rimLight);
          }
          // remove temporary procedural once GLB is in
          playerGroup.remove(proc);
          proc.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); });
          playerGroup.add(ship);
        },
        undefined,
        (err) => { console.warn("GLB load failed, keeping procedural fallback", err); }
      );
    };

    // Initial ship (procedural UFO — will be overridden by useEffect below if user has a skin equipped)
    gameRef.current.applySkin({ model_type: "procedural", color: "#334466", glow_color: "#33e0ff" });

    // Bullet pool + entity arrays
    const state = {
      player: playerGroup,
      playerPos: new THREE.Vector3(0, 0, 8),
      playerVel: new THREE.Vector3(0, 0, 0),
      invulnUntil: 0,
      bullets: [], // {mesh, vel, until}
      enemyBullets: [],
      asteroids: [], // {mesh, vel, r, hp, size}
      enemies: [], // {mesh, cd, hp}
      signals: [], // {mesh, phase}
      keys: {},
      touchInput: { x: 0, y: 0 },     // set by MobileControls; range [-1, 1]
      shooting: false,
      lastShot: 0,
      spawnTimers: { asteroid: 0, enemy: 0, signal: 0, comet: 25 + Math.random() * 20 },
      comet: null, // { mesh, vel, hp, r }
      level: 1,
      score: 0,
      lives: 3,
      collected: 0,
      goal: 5,
      state: "menu",
      bgTexIdx: 0,
    };
    gameRef.current.state = state;

    // Background is fixed (milky way texture) — setBackground() is a no-op now, kept for compat.
    const setBackground = (_idx) => { /* no-op — using fixed milky way texture */ };
    // Optional subtle color tint change per level (adds variety without swapping the panorama)
    const setLevelTint = (idx) => {
      const tints = [0xffffff, 0xeeddff, 0xddeeff, 0xffddcc, 0xccffee, 0xffeecc];
      bgSphere.material.color = new THREE.Color(tints[idx % tints.length]);
    };
    setLevelTint(0);

    // ─── entity factories ───
    const bulletGeo = new THREE.SphereGeometry(0.22, 12, 12);
    const bulletMat = new THREE.MeshBasicMaterial({ color: 0x66ffcc });
    const enemyBulletMat = new THREE.MeshBasicMaterial({ color: 0xff5555 });

    function shoot() {
      const now = performance.now();
      if (now - state.lastShot < 180) return;
      state.lastShot = now;
      const doubleActive = state.powerUps.double && now < state.powerUps.double;
      const shots = 1 + Math.floor((state.level - 1) / 4) + (doubleActive ? 1 : 0);
      for (let i = 0; i < shots; i++) {
        const m = new THREE.Mesh(bulletGeo, bulletMat);
        m.position.copy(state.player.position);
        m.position.z -= 1.2;
        const spread = shots > 1 ? (i - (shots - 1) / 2) * 0.12 : 0;
        scene.add(m);
        state.bullets.push({
          mesh: m,
          vel: new THREE.Vector3(spread * 24, 0, -70),
          until: now + 1400,
        });
        // NOTE: previously attached a PointLight per bullet. Removed for perf — each
        // dynamic light in Three.js forces a shader recompile + heavier fragment work.
        // The bullet mesh is already self-illuminated via MeshBasicMaterial (color: 0x66ffcc).
      }
    }

    // Preload rocky-asteroid GLB template once (Draco-compressed). Cloned at spawn time.
    let asteroidTemplate = null;
    let asteroidBaseScale = 1;
    {
      const astLoader = new GLTFLoader();
      const astDraco = new DRACOLoader();
      astDraco.setDecoderPath("/draco/");
      astLoader.setDRACOLoader(astDraco);
      astLoader.load("/models/rocky_asteroid.glb", (gltf) => {
        asteroidTemplate = gltf.scene;
        const box = new THREE.Box3().setFromObject(asteroidTemplate);
        const size = new THREE.Vector3(); box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        asteroidBaseScale = 2 / maxDim; // normalize so max dim = 2 units → r=1 gives ~1 unit radius
      });
    }

    function spawnAsteroid(size = null) {
      const s = size ?? (Math.random() < 0.5 ? "S" : Math.random() < 0.85 ? "M" : "L");
      const r = s === "S" ? 0.7 : s === "M" ? 1.1 : 1.7;
      const hp = s === "S" ? 1 : s === "M" ? 2 : 3;
      let m;
      if (asteroidTemplate) {
        m = asteroidTemplate.clone(true);
        m.scale.setScalar(asteroidBaseScale * r);
      } else {
        // Fallback while GLB still loads: neutral gray procedural rock
        const geo = new THREE.SphereGeometry(r, 16, 16);
        const mat = new THREE.MeshStandardMaterial({ color: 0x6b5544, roughness: 0.9, metalness: 0.1 });
        m = new THREE.Mesh(geo, mat);
      }
      m.position.set((Math.random() - 0.5) * B.x * 2, 0, -B.z - 2);
      m.userData.rot = new THREE.Vector3(Math.random() * 0.02, Math.random() * 0.02, Math.random() * 0.02);
      scene.add(m);
      const speed = 6 + state.level * 0.6 + Math.random() * 4;
      state.asteroids.push({
        mesh: m,
        vel: new THREE.Vector3((Math.random() - 0.5) * 3, 0, speed),
        r, hp, size: s,
      });
    }

    function spawnEnemy() {
      const grp = new THREE.Group();
      const body = new THREE.Mesh(
        new THREE.CylinderGeometry(0.9, 1.4, 0.3, 24),
        new THREE.MeshStandardMaterial({ color: 0xff3355, metalness: 0.7, roughness: 0.3, emissive: 0xff0044, emissiveIntensity: 1.4 })
      );
      grp.add(body);
      const d = new THREE.Mesh(
        new THREE.SphereGeometry(0.6, 20, 20, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: 0xff77aa, emissive: 0xff3366, emissiveIntensity: 1.6, transparent: true, opacity: 0.85 })
      );
      d.position.y = 0.14;
      grp.add(d);
      // PointLight removed for perf — enemy body/dome are already emissive.
      grp.position.set((Math.random() - 0.5) * B.x * 1.4, 0, -B.z - 3);
      scene.add(grp);
      state.enemies.push({ mesh: grp, cd: 1200, hp: 2 + Math.floor(state.level / 3) });
    }

    const POWERUP_TYPES = ["shield", "double", "speed"];
    const POWERUP_COLORS = { shield: 0xffcc00, double: 0x66ff66, speed: 0x66ccff };

    function spawnSignal(forcePowerUp = null) {
      const grp = new THREE.Group();
      // 15% chance a signal is actually a power-up (or forced)
      const powerUp = forcePowerUp || (Math.random() < 0.15 ? POWERUP_TYPES[Math.floor(Math.random() * POWERUP_TYPES.length)] : null);

      if (powerUp) {
        const c = POWERUP_COLORS[powerUp];
        const orb = new THREE.Mesh(
          new THREE.IcosahedronGeometry(0.6, 0),
          new THREE.MeshBasicMaterial({ color: c })
        );
        grp.add(orb);
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(1.0, 0.06, 10, 32),
          new THREE.MeshBasicMaterial({ color: c, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false })
        );
        ring.rotation.x = Math.PI / 2;
        grp.add(ring);
        // PointLight removed for perf — orb is self-lit via MeshBasicMaterial.
      } else {
        const base = new THREE.Mesh(
          new THREE.ConeGeometry(0.55, 0.6, 20, 1, true),
          new THREE.MeshBasicMaterial({ color: 0x33ff99, side: THREE.DoubleSide })
        );
        base.rotation.x = Math.PI;
        grp.add(base);
        const antenna = new THREE.Mesh(
          new THREE.CylinderGeometry(0.05, 0.05, 0.7, 8),
          new THREE.MeshBasicMaterial({ color: 0xaaffee })
        );
        antenna.position.y = 0.35;
        grp.add(antenna);
        const halo = new THREE.Mesh(
          new THREE.TorusGeometry(0.9, 0.05, 8, 32),
          new THREE.MeshBasicMaterial({ color: 0x66ffcc, transparent: true, opacity: 0.5 })
        );
        halo.rotation.x = Math.PI / 2;
        grp.add(halo);
        // PointLight removed for perf — mesh is self-lit.
      }
      grp.position.set((Math.random() - 0.5) * B.x * 1.6, 0, -B.z - Math.random() * 3);
      scene.add(grp);
      state.signals.push({ mesh: grp, phase: Math.random() * Math.PI * 2, powerUp });
    }

    // ─── BOSS — spawns every 5 levels ───
    function spawnBoss(level) {
      if (state.boss) return;
      const grp = new THREE.Group();
      // Central hull (dark ominous mothership)
      const hull = new THREE.Mesh(
        new THREE.OctahedronGeometry(2.3, 1),
        new THREE.MeshStandardMaterial({ color: 0x1a0022, metalness: 0.85, roughness: 0.3, emissive: 0x440066, emissiveIntensity: 0.9 })
      );
      grp.add(hull);
      const ringDeco = new THREE.Mesh(
        new THREE.TorusGeometry(3.2, 0.18, 12, 48),
        new THREE.MeshStandardMaterial({ color: 0x772266, emissive: 0xaa2288, emissiveIntensity: 1.2, metalness: 0.7, roughness: 0.3 })
      );
      ringDeco.rotation.x = Math.PI / 2;
      grp.add(ringDeco);
      // 3 weak points arranged around
      const numWp = 3;
      const wpHp = 4 + Math.floor(level / 5) * 2; // scales harder each boss level
      const weakPoints = [];
      for (let i = 0; i < numWp; i++) {
        const angle = (i / numWp) * Math.PI * 2;
        const wp = new THREE.Mesh(
          new THREE.SphereGeometry(0.75, 20, 20),
          new THREE.MeshStandardMaterial({ color: 0xff6a00, emissive: 0xff9944, emissiveIntensity: 2.4, metalness: 0.5, roughness: 0.35 })
        );
        wp.userData.angle = angle;
        wp.userData.hp = wpHp;
        wp.position.set(Math.cos(angle) * 3.2, 0.1, Math.sin(angle) * 3.2);
        // per-weak-point PointLight removed for perf — center boss light covers illumination
        grp.add(wp);
        weakPoints.push(wp);
      }
      const centerLight = new THREE.PointLight(0xaa44ff, 5, 22);
      grp.add(centerLight);
      grp.position.set(0, 0.2, -B.z + 6);
      scene.add(grp);
      const totalHp = wpHp * numWp;
      state.boss = { group: grp, hp: totalHp, maxHp: totalHp, weakPoints, cd: 1400, phase: 0 };
      setHud((h) => ({ ...h, boss: { hp: totalHp, maxHp: totalHp }, bossWarning: true }));
      setTimeout(() => setHud((h) => ({ ...h, bossWarning: false })), 3500);
    }

    // ─── Comet — rare bonus event ───
    function spawnComet() {
      if (state.comet) return; // one at a time
      const grp = new THREE.Group();
      const head = new THREE.Mesh(
        new THREE.SphereGeometry(1.1, 24, 24),
        new THREE.MeshBasicMaterial({ color: 0xccffff })
      );
      grp.add(head);
      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(2.4, 24, 24),
        new THREE.MeshBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.28, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.BackSide })
      );
      grp.add(glow);
      const light = new THREE.PointLight(0xaaeeff, 6, 30);
      grp.add(light);
      // Comet tail — a cone stretched backwards
      const tail = new THREE.Mesh(
        new THREE.ConeGeometry(1.4, 9, 22, 1, true),
        new THREE.MeshBasicMaterial({ color: 0x88ddff, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide })
      );
      // Cone default apex on +Y; we'll orient so the base is behind the direction of travel
      tail.rotation.z = Math.PI / 2; // now apex on +X
      tail.position.x = -4.5; // sits behind head
      grp.add(tail);

      // Trajectory: cross from one side to the other, roughly across the play field
      const fromLeft = Math.random() < 0.5;
      const startX = fromLeft ? -B.x - 6 : B.x + 6;
      const startZ = -B.z - 2 + Math.random() * 6;
      const endX = fromLeft ? B.x + 6 : -B.x - 6;
      const endZ = startZ + 10 + Math.random() * 8;
      grp.position.set(startX, 0, startZ);
      const dx = endX - startX;
      const dz = endZ - startZ;
      const dist = Math.hypot(dx, dz);
      const speed = 20;
      const vel = new THREE.Vector3((dx / dist) * speed, 0, (dz / dist) * speed);
      // Rotate the group so the tail extends behind (opposite to velocity along XZ plane)
      // heading angle (0 rad = +X)
      const heading = Math.atan2(vel.z, vel.x);
      grp.rotation.y = -heading; // rotate Y so local +X points along velocity
      scene.add(grp);
      state.comet = { mesh: grp, vel, hp: 5, r: 1.3 };
      setHud((h) => ({ ...h, cometWarning: true }));
      // Auto-clear warning after 3s
      setTimeout(() => setHud((h) => ({ ...h, cometWarning: false })), 3000);
    }

    function dropSignalsAt(pos, count = 6) {
      for (let i = 0; i < count; i++) {
        spawnSignal();
        const sig = state.signals[state.signals.length - 1];
        sig.mesh.position.set(
          pos.x + (Math.random() - 0.5) * 5,
          0,
          pos.z + (Math.random() - 0.5) * 3
        );
      }
    }

    // ─── controls ───
    const onKeyDown = (e) => {
      state.keys[e.code] = true;
      if (e.code === "Space") { e.preventDefault(); state.shooting = true; }
      if (e.code === "KeyP" || e.code === "Escape") pause();
    };
    const onKeyUp = (e) => {
      state.keys[e.code] = false;
      if (e.code === "Space") state.shooting = false;
    };
    const onPointerDown = () => { if (state.state === "playing") state.shooting = true; };
    const onPointerUp = () => { state.shooting = false; };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);

    // Reset
    gameRef.current.reset = () => {
      [...state.bullets, ...state.enemyBullets, ...state.asteroids, ...state.enemies, ...state.signals].forEach((e) => scene.remove(e.mesh));
      if (state.comet) { scene.remove(state.comet.mesh); state.comet = null; }
      if (state.boss) { scene.remove(state.boss.group); state.boss = null; }
      state.powerUps = {};
      state.bullets.length = 0;
      state.enemyBullets.length = 0;
      state.asteroids.length = 0;
      state.enemies.length = 0;
      state.signals.length = 0;
      state.playerPos.set(0, 0, 8);
      state.playerVel.set(0, 0, 0);
      state.player.position.copy(state.playerPos);
      state.player.rotation.set(0, 0, 0);
      state.score = 0;
      state.lives = 3;
      state.level = 1;
      state.collected = 0;
      state.goal = 5;
      state.invulnUntil = 0;
      state.spawnTimers.comet = 25 + Math.random() * 20;
      state.state = "playing";
      setBackground(0);
      setLevelTint(0);
    };

    // ─── main loop ───
    let running = true;
    const clock = new THREE.Clock();
    const tmpV = new THREE.Vector3();

    function nextLevel() {
      state.level += 1;
      state.collected = 0;
      const isBossLevel = state.level % 5 === 0;
      state.goal = isBossLevel ? 999 : (5 + state.level * 2); // boss levels don't use signal goal
      setBackground(state.level);
      setLevelTint(state.level);
      setHud((h) => ({ ...h, level: state.level, signals: 0, signalsGoal: state.goal, state: "playing" }));
      if (isBossLevel) {
        // Small delay so the level transition flash reads naturally
        setTimeout(() => { if (state.state === "playing" && !state.boss) spawnBoss(state.level); }, 400);
      }
    }

    function damagePlayer() {
      const now = performance.now();
      if (now < state.invulnUntil) return;
      // Shield power-up absorbs damage
      if (state.powerUps.shield && now < state.powerUps.shield) {
        state.invulnUntil = now + 400; // small cooldown after absorb
        return;
      }
      state.lives -= 1;
      state.invulnUntil = now + 1600;
      if (state.lives <= 0) {
        state.state = "gameover";
        setHud((h) => ({ ...h, lives: 0, state: "gameover", score: state.score, level: state.level }));
      } else {
        setHud((h) => ({ ...h, lives: state.lives }));
      }
    }

    function activatePowerUp(type) {
      const now = performance.now();
      const duration = 8000;
      state.powerUps[type] = now + duration;
      setHud((h) => ({ ...h, activeBuffs: { ...(h.activeBuffs || {}), [type]: now + duration } }));
    }

    const loop = () => {
      if (!running) return;
      const dt = Math.min(0.05, clock.getDelta());
      const now = performance.now();

      // Constant baseline drift so it always feels alive (deep space "cruising")
      bgSphere.rotation.y += 0.00015;
      dustSphere.rotation.y -= 0.00025; // parallax opposite direction
      dustSphere.rotation.x += 0.0001;

      // Motion intensity from player velocity (0..1) — only during play
      const velLen = state.state === "playing" ? Math.hypot(state.playerVel.x, state.playerVel.z) : 0;
      const moveIntensity = Math.min(1, velLen / 22);

      // Extra rotation on movement — parallax reacts to input for a "flying through space" feel
      const velX = state.state === "playing" ? state.playerVel.x : 0;
      const velZ = state.state === "playing" ? state.playerVel.z : 0;
      bgSphere.rotation.y += -velX * 0.00008;
      bgSphere.rotation.x += -velZ * 0.00006;

      if (state.state === "playing") {
        // Input → player velocity (keyboard OR touch joystick)
        let ax = 0, az = 0;
        if (state.keys["ArrowLeft"] || state.keys["KeyA"]) ax -= 1;
        if (state.keys["ArrowRight"] || state.keys["KeyD"]) ax += 1;
        if (state.keys["ArrowUp"] || state.keys["KeyW"]) az -= 1;
        if (state.keys["ArrowDown"] || state.keys["KeyS"]) az += 1;
        // Touch joystick contributes when keyboard doesn't (both work but keyboard wins on ties)
        if (state.touchInput && (ax === 0 && az === 0)) {
          ax = state.touchInput.x;
          az = state.touchInput.y;
        }
        const len = Math.hypot(ax, az) || 1;
        const nowMs = performance.now();
        const speedActive = state.powerUps.speed && nowMs < state.powerUps.speed;
        const speed = speedActive ? 34 : 22;
        state.playerVel.x = (ax / len) * speed;
        state.playerVel.z = (az / len) * speed;
        state.playerPos.x = Math.max(-B.x, Math.min(B.x, state.playerPos.x + state.playerVel.x * dt));
        state.playerPos.z = Math.max(-B.z + 6, Math.min(B.z, state.playerPos.z + state.playerVel.z * dt));
        state.player.position.copy(state.playerPos);
        state.player.rotation.z = -ax * 0.35;
        state.player.rotation.x = az * 0.2;
        // no auto Y spin for the ship (would break its orientation)

        // Invuln blink
        state.player.visible = now > state.invulnUntil || Math.floor(now / 80) % 2 === 0;

        // Shoot
        if (state.shooting) shoot();

        // Spawns
        state.spawnTimers.asteroid -= dt;
        state.spawnTimers.enemy -= dt;
        state.spawnTimers.signal -= dt;
        if (state.spawnTimers.asteroid <= 0) {
          spawnAsteroid();
          state.spawnTimers.asteroid = Math.max(0.35, 1.2 - state.level * 0.08);
        }
        if (state.spawnTimers.enemy <= 0 && state.level >= 2 && !state.boss) {
          spawnEnemy();
          state.spawnTimers.enemy = Math.max(2, 5.5 - state.level * 0.3);
        }
        if (state.spawnTimers.signal <= 0) {
          spawnSignal();
          state.spawnTimers.signal = 2.5 + Math.random() * 2;
        }
        state.spawnTimers.comet -= dt;
        if (state.spawnTimers.comet <= 0 && !state.comet && state.level >= 2) {
          spawnComet();
          state.spawnTimers.comet = 35 + Math.random() * 30; // next comet in 35-65s
        }

        // Bullets
        for (let i = state.bullets.length - 1; i >= 0; i--) {
          const b = state.bullets[i];
          b.mesh.position.addScaledVector(b.vel, dt);
          if (now > b.until || b.mesh.position.z < -B.z - 5) {
            scene.remove(b.mesh); b.mesh.geometry.dispose && b.mesh.geometry.dispose(); state.bullets.splice(i, 1);
          }
        }

        // Enemy bullets
        for (let i = state.enemyBullets.length - 1; i >= 0; i--) {
          const b = state.enemyBullets[i];
          b.mesh.position.addScaledVector(b.vel, dt);
          if (now > b.until || Math.abs(b.mesh.position.z) > B.z + 5) {
            scene.remove(b.mesh); state.enemyBullets.splice(i, 1); continue;
          }
          // player hit
          if (now > state.invulnUntil && b.mesh.position.distanceTo(state.playerPos) < 1.2) {
            scene.remove(b.mesh); state.enemyBullets.splice(i, 1);
            damagePlayer();
          }
        }

        // Asteroids
        for (let i = state.asteroids.length - 1; i >= 0; i--) {
          const a = state.asteroids[i];
          a.mesh.position.addScaledVector(a.vel, dt);
          a.mesh.rotation.x += a.mesh.userData.rot.x;
          a.mesh.rotation.y += a.mesh.userData.rot.y;
          a.mesh.rotation.z += a.mesh.userData.rot.z;
          if (a.mesh.position.z > B.z + 5) {
            scene.remove(a.mesh); state.asteroids.splice(i, 1); continue;
          }
          // vs bullets
          for (let j = state.bullets.length - 1; j >= 0; j--) {
            const b = state.bullets[j];
            if (b.mesh.position.distanceTo(a.mesh.position) < a.r + 0.3) {
              scene.remove(b.mesh); state.bullets.splice(j, 1);
              a.hp -= 1;
              if (a.hp <= 0) {
                scene.remove(a.mesh);
                state.asteroids.splice(i, 1);
                const pts = a.size === "S" ? 10 : a.size === "M" ? 20 : 35;
                state.score += pts * Math.max(1, Math.floor(state.level / 3));
                if (a.size !== "S") {
                  for (let k = 0; k < 2; k++) {
                    spawnAsteroid(a.size === "L" ? "M" : "S");
                    const child = state.asteroids[state.asteroids.length - 1];
                    child.mesh.position.copy(a.mesh.position);
                    child.vel.x = (Math.random() - 0.5) * 8;
                    child.vel.z = 3 + Math.random() * 4;
                  }
                }
                setHud((h) => ({ ...h, score: state.score }));
              }
              break;
            }
          }
          // vs player
          if (now > state.invulnUntil && a.mesh.position.distanceTo(state.playerPos) < a.r + 1.1) {
            scene.remove(a.mesh); state.asteroids.splice(i, 1);
            damagePlayer();
          }
        }

        // Enemies
        for (let i = state.enemies.length - 1; i >= 0; i--) {
          const e = state.enemies[i];
          // move: approach player then strafe
          tmpV.copy(state.playerPos).sub(e.mesh.position);
          tmpV.y = 0;
          const dz = tmpV.z;
          e.mesh.position.z += Math.sign(dz) * 4 * dt;
          e.mesh.position.x += Math.sin(now * 0.0015 + i) * 6 * dt;
          e.mesh.rotation.y += dt * 3;
          e.cd -= dt * 1000;
          if (e.cd <= 0 && e.mesh.position.z < state.playerPos.z) {
            e.cd = 1400 - Math.min(700, state.level * 60);
            // shoot at player
            const dir = new THREE.Vector3().subVectors(state.playerPos, e.mesh.position).normalize().multiplyScalar(30);
            const m = new THREE.Mesh(bulletGeo, enemyBulletMat);
            m.position.copy(e.mesh.position);
            scene.add(m);
            state.enemyBullets.push({ mesh: m, vel: dir, until: now + 3200 });
          }
          if (e.mesh.position.z > B.z + 5) {
            scene.remove(e.mesh); state.enemies.splice(i, 1); continue;
          }
          // vs bullets
          for (let j = state.bullets.length - 1; j >= 0; j--) {
            const b = state.bullets[j];
            if (b.mesh.position.distanceTo(e.mesh.position) < 1.2) {
              scene.remove(b.mesh); state.bullets.splice(j, 1);
              e.hp -= 1;
              if (e.hp <= 0) {
                scene.remove(e.mesh); state.enemies.splice(i, 1);
                state.score += 50 * Math.max(1, Math.floor(state.level / 3));
                setHud((h) => ({ ...h, score: state.score }));
              }
              break;
            }
          }
          // vs player
          if (now > state.invulnUntil && e.mesh.position.distanceTo(state.playerPos) < 1.6) {
            scene.remove(e.mesh); state.enemies.splice(i, 1);
            damagePlayer();
          }
        }

        // Signals
        for (let i = state.signals.length - 1; i >= 0; i--) {
          const s = state.signals[i];
          s.phase += dt * 2;
          s.mesh.position.z += 4 * dt;
          s.mesh.position.y = Math.sin(s.phase) * 0.4;
          s.mesh.rotation.y += dt * 2;
          if (s.mesh.position.z > B.z + 4) {
            scene.remove(s.mesh); state.signals.splice(i, 1); continue;
          }
          if (s.mesh.position.distanceTo(state.playerPos) < 1.6) {
            scene.remove(s.mesh); state.signals.splice(i, 1);
            if (s.powerUp) {
              activatePowerUp(s.powerUp);
              state.score += 200;
              setHud((h) => ({ ...h, score: state.score }));
            } else {
              state.collected += 1;
              state.score += 100 + state.level * 20;
              setHud((h) => ({ ...h, score: state.score, signals: state.collected }));
              if (state.collected >= state.goal) nextLevel();
            }
          }
        }

        // Comet — rare bonus event. Traverses map linearly; on death drops many signals.
        if (state.comet) {
          const c = state.comet;
          c.mesh.position.addScaledVector(c.vel, dt);
          c.mesh.rotation.x += dt * 3; // subtle head spin
          // Off-screen removal
          const off = Math.abs(c.mesh.position.x) > B.x + 12 || c.mesh.position.z > B.z + 10;
          if (off) {
            scene.remove(c.mesh); state.comet = null;
          } else {
            // vs bullets
            for (let j = state.bullets.length - 1; j >= 0; j--) {
              const b = state.bullets[j];
              if (b.mesh.position.distanceTo(c.mesh.position) < c.r + 0.5) {
                scene.remove(b.mesh); state.bullets.splice(j, 1);
                c.hp -= 1;
                if (c.hp <= 0) {
                  const pos = c.mesh.position.clone();
                  scene.remove(c.mesh);
                  state.comet = null;
                  state.score += 500 + state.level * 100;
                  dropSignalsAt(pos, 8);
                  setHud((h) => ({ ...h, score: state.score, cometDown: true }));
                  setTimeout(() => setHud((h) => ({ ...h, cometDown: false })), 2000);
                }
                break;
              }
            }
          }
        }

        // Boss update — heavy encounter with orbiting weak points
        if (state.boss) {
          const b = state.boss;
          b.phase += dt;
          // Sinusoidal horizontal drift
          b.group.position.x = Math.sin(b.phase * 0.5) * (B.x - 4);
          b.group.rotation.y += dt * 0.35;
          // Orbit weak points (still-attached children)
          b.weakPoints.forEach((wp) => {
            if (!wp.parent) return;
            wp.userData.angle += dt * 1.2;
            const r = 3.2;
            wp.position.x = Math.cos(wp.userData.angle) * r;
            wp.position.z = Math.sin(wp.userData.angle) * r;
          });
          // Shoot pattern
          b.cd -= dt * 1000;
          if (b.cd <= 0) {
            b.cd = Math.max(500, 1200 - state.level * 20);
            const baseDir = new THREE.Vector3().subVectors(state.playerPos, b.group.position).normalize();
            for (let a = -1; a <= 1; a++) {
              const angleOff = a * 0.22;
              const s = Math.sin(angleOff), c = Math.cos(angleOff);
              const nd = new THREE.Vector3(baseDir.x * c - baseDir.z * s, 0, baseDir.x * s + baseDir.z * c).multiplyScalar(28);
              const m = new THREE.Mesh(bulletGeo, enemyBulletMat);
              m.position.copy(b.group.position);
              scene.add(m);
              state.enemyBullets.push({ mesh: m, vel: nd, until: now + 4200 });
            }
          }
          // vs player bullets (only weak points take damage)
          const wpWorld = new THREE.Vector3();
          for (let j = state.bullets.length - 1; j >= 0; j--) {
            const bl = state.bullets[j];
            let hit = false;
            for (let k = 0; k < b.weakPoints.length; k++) {
              const wp = b.weakPoints[k];
              if (!wp.parent) continue;
              wp.getWorldPosition(wpWorld);
              if (bl.mesh.position.distanceTo(wpWorld) < 1.05) {
                scene.remove(bl.mesh); state.bullets.splice(j, 1);
                wp.userData.hp -= 1;
                b.hp -= 1;
                setHud((h) => ({ ...h, boss: { hp: b.hp, maxHp: b.maxHp } }));
                if (wp.userData.hp <= 0) {
                  b.group.remove(wp);
                  wp.userData.destroyed = true;
                }
                if (b.hp <= 0) {
                  scene.remove(b.group);
                  state.boss = null;
                  state.score += 2000 + state.level * 200;
                  setHud((h) => ({ ...h, boss: null, bossDown: true, score: state.score }));
                  setTimeout(() => setHud((h) => ({ ...h, bossDown: false })), 3000);
                  // Force level advancement
                  nextLevel();
                }
                hit = true;
                break;
              }
            }
            if (hit) continue;
          }
          // Boss body vs player (collision damage)
          if (now > state.invulnUntil && b.group.position.distanceTo(state.playerPos) < 3.2) {
            damagePlayer();
          }
        }

        // Power-up expiry cleanup
        {
          const t = performance.now();
          let dirty = false;
          for (const k of Object.keys(state.powerUps)) {
            if (state.powerUps[k] < t) { delete state.powerUps[k]; dirty = true; }
          }
          if (dirty) {
            setHud((h) => {
              const nb = {};
              Object.keys(state.powerUps).forEach((kk) => { nb[kk] = state.powerUps[kk]; });
              return { ...h, activeBuffs: nb };
            });
          }
        }

        // Camera follows player slightly
        camera.position.x = state.playerPos.x * 0.15;
        camera.position.z = 12 + state.playerPos.z * 0.15;
        camera.lookAt(state.playerPos.x * 0.15, 0, state.playerPos.z * 0.15);
      }

      renderer.render(scene, camera);
      requestAnimationFrame(loop);
    };
    loop();

    const onResize = () => {
      if (!mount) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(Math.floor(mount.clientWidth * RES_SCALE), Math.floor(mount.clientHeight * RES_SCALE), false);
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";
    };
    window.addEventListener("resize", onResize);

    return () => {
      running = false;
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, [pause]);

  // sync visible game state
  useEffect(() => {
    if (gameRef.current.state) gameRef.current.state.state = hud.state;
  }, [hud.state]);

  // Fetch equipped skin when user is authenticated and apply it to the ship
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [catRes, meRes] = await Promise.all([
          fetch(`${API}/api/shop/skins`).then((r) => r.json()),
          fetch(`${API}/api/shop/me/skins`, { credentials: "include" }).then((r) => r.json()),
        ]);
        if (cancelled) return;
        const skin = (catRes.skins || []).find((s) => s.skin_id === meRes.equipped);
        if (skin && gameRef.current.applySkin) {
          gameRef.current.applySkin(skin);
          setCurrentSkin(skin);
        }
      } catch (err) {
        console.warn("[pilot] failed to load equipped skin:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Auto-open shop when redirected back from payment success/cancel
  useEffect(() => {
    if (searchParams.get("openShop") === "1") {
      setShopOpen(true);
      // clean query
      const next = new URLSearchParams(searchParams);
      next.delete("openShop");
      next.delete("justBought");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSkinChange = useCallback((skin) => {
    if (skin && gameRef.current.applySkin) {
      gameRef.current.applySkin(skin);
      setCurrentSkin(skin);
    }
  }, []);

  return (
    <div className="fixed inset-0 text-white overflow-hidden" data-testid="pilot-game-root">
      {/* Background image — deep-space Milky Way texture provided by the user */}
      <img
        src="/stars_milky_way.webp"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none z-0"
      />
      <div
        ref={mountRef}
        className="absolute inset-0"
      />

      {/* HUD */}
      <div className="absolute top-0 left-0 right-0 z-40 p-4 flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-3 pointer-events-auto">
          <button
            onClick={() => nav("/")}
            data-testid="pilot-back"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-primary/40 bg-black/60 backdrop-blur hover:bg-primary/10 font-mono text-[11px] tracking-widest text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> SITE
          </button>
          <div className="hidden sm:flex items-center gap-4 px-3 py-2 rounded-sm border border-primary/30 bg-black/60 backdrop-blur font-mono text-[11px] tracking-widest">
            <span data-testid="hud-score" className="text-primary">SCORE <span className="text-white">{hud.score}</span></span>
            <span data-testid="hud-level" className="text-secondary">LV <span className="text-white">{hud.level}</span></span>
            <span data-testid="hud-signals" className="text-primary">📡 <span className="text-white">{hud.signals}/{hud.signalsGoal}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          <div className="flex items-center gap-1 px-3 py-2 rounded-sm border border-primary/30 bg-black/60 backdrop-blur">
            {Array.from({ length: 3 }).map((_, i) => (
              <Heart key={`heart-${i}`} className={`h-4 w-4 ${i < hud.lives ? "text-red-400 fill-red-400" : "text-red-900"}`} />
            ))}
          </div>
          {hud.state === "playing" && (
            <button onClick={pause} data-testid="pilot-pause" className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-primary/40 bg-black/60 backdrop-blur hover:bg-primary/10 font-mono text-[11px] tracking-widest text-primary">
              <Pause className="h-3.5 w-3.5" /> PAUSE
            </button>
          )}
          <button
            onClick={() => setShopOpen(true)}
            data-testid="pilot-open-shop"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-secondary/50 bg-black/60 backdrop-blur hover:bg-secondary/10 font-mono text-[11px] tracking-widest text-secondary"
          >
            <ShoppingBag className="h-3.5 w-3.5" /> {t("pilot.shop_btn")}
          </button>
          {FEATURES.rooms && (
            <button
              onClick={() => nav("/rooms")}
              data-testid="pilot-open-rooms"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-amber-300/60 bg-black/60 backdrop-blur hover:bg-amber-500/10 font-mono text-[11px] tracking-widest text-amber-200"
            >
              <Trophy className="h-3.5 w-3.5" /> {t("pilot.rooms_btn")}
            </button>
          )}
          <WalletBadge compact />
        </div>
      </div>

      {/* Comet event banners */}
      {hud.state === "playing" && hud.cometWarning && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
             data-testid="comet-warning">
          <div className="px-5 py-2 rounded-sm border-2 border-cyan-300 bg-cyan-500/10 backdrop-blur-sm animate-pulse">
            <div className="font-display font-black text-2xl tracking-tighter text-cyan-200 drop-shadow-[0_0_12px_rgba(103,232,249,0.9)]">
              {t("pilot.comet_detected")}
            </div>
          </div>
        </div>
      )}
      {hud.state === "playing" && hud.cometDown && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
             data-testid="comet-down">
          <div className="px-5 py-2 rounded-sm border-2 border-emerald-300 bg-emerald-500/10 backdrop-blur-sm">
            <div className="font-display font-black text-2xl tracking-tighter text-emerald-200 drop-shadow-[0_0_12px_rgba(110,231,183,0.9)]">
              {t("pilot.comet_destroyed")}
            </div>
          </div>
        </div>
      )}

      {/* Boss warning + boss defeated banners */}
      {hud.state === "playing" && hud.bossWarning && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
             data-testid="boss-warning">
          <div className="px-6 py-3 rounded-sm border-2 border-red-500 bg-red-600/15 backdrop-blur-sm animate-pulse">
            <div className="font-display font-black text-3xl tracking-tighter text-red-200 drop-shadow-[0_0_16px_rgba(239,68,68,1)]">
              {t("pilot.boss_incoming")}
            </div>
          </div>
        </div>
      )}
      {hud.state === "playing" && hud.bossDown && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
             data-testid="boss-down">
          <div className="px-6 py-3 rounded-sm border-2 border-yellow-300 bg-yellow-500/15 backdrop-blur-sm">
            <div className="font-display font-black text-3xl tracking-tighter text-yellow-200 drop-shadow-[0_0_16px_rgba(253,224,71,0.9)]">
              {t("pilot.boss_down")}
            </div>
          </div>
        </div>
      )}

      {/* Boss health bar (top-center, above comet banner slot) */}
      {hud.state === "playing" && hud.boss && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 z-40 w-[min(70vw,560px)] pointer-events-none"
             data-testid="boss-health-bar">
          <div className="flex items-center justify-between font-mono text-[10px] tracking-widest text-red-200 mb-1">
            <span>{t("pilot.boss_label")}</span>
            <span>{hud.boss.hp}/{hud.boss.maxHp} HP</span>
          </div>
          <div className="w-full h-2 rounded-sm border border-red-400/70 bg-black/70 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-red-500 via-orange-400 to-yellow-300 transition-all"
                 style={{ width: `${Math.max(0, (hud.boss.hp / hud.boss.maxHp) * 100)}%` }} />
          </div>
        </div>
      )}

      {/* Active power-up indicators (bottom-left) */}
      {hud.state === "playing" && hud.activeBuffs && Object.keys(hud.activeBuffs).length > 0 && (
        <div className="absolute bottom-6 left-6 z-40 flex flex-col gap-1 pointer-events-none"
             data-testid="active-buffs">
          {Object.entries(hud.activeBuffs).map(([type, expiresAt]) => {
            const cfg = {
              shield: { label: "ESCUDO",       cls: "border-yellow-300 text-yellow-200 shadow-yellow-300/40" },
              double: { label: "TIRO DUPLO",   cls: "border-emerald-300 text-emerald-200 shadow-emerald-300/40" },
              speed:  { label: "IMPULSO",      cls: "border-cyan-300 text-cyan-200 shadow-cyan-300/40" },
            }[type] || { label: type.toUpperCase(), cls: "border-primary text-primary shadow-primary/40" };
            const secondsLeft = Math.max(0, Math.ceil((expiresAt - performance.now()) / 1000));
            return (
              <div key={type} data-testid={`buff-${type}`}
                   className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border-2 bg-black/70 backdrop-blur font-mono text-[10px] tracking-widest ${cfg.cls} shadow-md`}>
                <span>{cfg.label}</span>
                <span className="opacity-80">{secondsLeft}s</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Tournament HUD — anchored center-top */}
      {activeTournament && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-40 pointer-events-auto">
          <TournamentHUD active={activeTournament} />
        </div>
      )}

      {/* Tournament PENDING HUD — user joined but sala not started yet */}
      {!activeTournament && pendingTournament && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-40 pointer-events-auto">
          <TournamentPendingHUD pending={pendingTournament} />
        </div>
      )}

      {/* Tournament Live Leaderboard — top-right, only during a run */}
      {activeTournament && hud.state === "playing" && (
        <div className="hidden md:block absolute top-32 right-4 z-30 pointer-events-none">
          <TournamentLiveBoard liveBoard={tournamentLiveBoard} currentUserId={user?.user_id} />
        </div>
      )}

      {/* Mobile HUD summary (below top bar on small screens) */}
      <div className="sm:hidden absolute top-16 left-4 right-4 z-20 flex justify-between font-mono text-[10px] tracking-widest text-primary pointer-events-none">
        <span>SCORE {hud.score}</span>
        <span>LV {hud.level}</span>
        <span>📡 {hud.signals}/{hud.signalsGoal}</span>
      </div>

      {/* Controls hint */}
      <div className="absolute bottom-4 right-4 z-20 text-right font-mono text-[9px] tracking-widest text-primary/50 pointer-events-none">
        {t("pilot.controls_wasd")}<br />
        {t("pilot.controls_shoot")}<br />
        {t("pilot.controls_pause")}
      </div>

      {/* MENU / GAME OVER / PAUSED overlays */}
      {(hud.state === "menu" || hud.state === "gameover" || hud.state === "paused") && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 backdrop-blur-sm pointer-events-auto p-6">
          <div className="max-w-md w-full rounded-sm border border-primary/40 bg-black/90 p-8 space-y-6" data-testid="pilot-overlay">
            {hud.state === "menu" && (
              <>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">{t("pilot.mission_label")}</p>
                  <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">
                    {t("pilot.title_l1")}<br /><span className="text-primary neon-glow-primary">{t("pilot.title_l2")}</span>
                  </h1>
                  <p className="mt-3 text-sm text-muted-foreground">
                    {t("pilot.intro_pre")}<span className="text-primary">{t("pilot.intro_signals")}</span>{t("pilot.intro_post")}
                  </p>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-muted-foreground">{t("pilot.pilot_name_label")}</label>
                  {passportLocked ? (
                    <div className="mt-2 w-full rounded-sm border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 font-mono text-sm text-emerald-300 flex items-center gap-2" data-testid="pilot-name-locked">
                      <span className="text-emerald-400">👽</span>
                      <span className="tracking-widest">{pilotName}</span>
                      <span className="ml-auto text-[9px] tracking-widest text-emerald-400/70">// PASSAPORTE</span>
                    </div>
                  ) : (
                    <>
                      <input
                        value={pilotName}
                        onChange={(e) => setPilotName(e.target.value.slice(0, 14))}
                        placeholder={t("pilot.pilot_name_placeholder")}
                        data-testid="pilot-name-input"
                        className="mt-2 w-full rounded-sm border border-primary/30 bg-black/60 px-3 py-2 font-mono text-sm text-foreground focus:outline-none focus:border-primary"
                      />
                      {user && (
                        <p className="mt-1 font-mono text-[9px] tracking-widest text-amber-300/80">
                          💡 Crie seu Alien Passport na home pra fixar seu callsign
                        </p>
                      )}
                    </>
                  )}
                </div>
                <button
                  onClick={startGame}
                  data-testid="pilot-start"
                  className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-xs px-5 py-3"
                >
                  {t("pilot.start_mission")}
                </button>
                <button
                  onClick={() => setShopOpen(true)}
                  data-testid="pilot-menu-open-shop"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-secondary/50 hover:bg-secondary/10 text-secondary font-mono tracking-widest text-xs px-5 py-3"
                >
                  <ShoppingBag className="h-3.5 w-3.5" /> {t("pilot.ship_shop")}
                </button>
                {user && myStats && myStats.best_score > 0 && (
                  <button
                    onClick={() => setShareOpen(true)}
                    data-testid="pilot-menu-share-rank"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-primary/40 hover:bg-primary/10 text-primary font-mono tracking-widest text-xs px-5 py-3"
                  >
                    <Share2 className="h-3.5 w-3.5" /> {t("pilot.share_rank")}
                  </button>
                )}
                {globalLb.length > 0 && (
                  <div data-testid="pilot-global-lb">
                    <p className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-muted-foreground mb-2">
                      <Trophy className="h-3.5 w-3.5 text-secondary" /> {t("pilot.top_pilots_global")}
                    </p>
                    <ul className="space-y-1">
                      {globalLb.slice(0, 10).map((l, i) => {
                        const isMe = user && l.user_id === user.user_id;
                        return (
                          <li key={l.user_id || i} className={`flex justify-between font-mono text-xs px-2 py-1 rounded-sm ${isMe ? "bg-primary/10 border border-primary/30" : ""}`}>
                            <span className={isMe ? "text-primary font-black" : "text-primary"}>
                              {l.rank}. {l.display_name}{isMe ? " ★" : ""}
                            </span>
                            <span className="text-muted-foreground">
                              {l.best_score.toLocaleString()} · LV{l.best_level} · <span className="text-secondary">{l.tier.label}</span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {leaderboard.length > 0 && (
                  <div>
                    <p className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-muted-foreground mb-2">
                      <Trophy className="h-3.5 w-3.5 text-secondary" /> {t("pilot.local_scores")}
                    </p>
                    <ul className="space-y-1">
                      {leaderboard.slice(0, 5).map((l, i) => (
                        <li key={`${l.name}-${l.at ?? i}`} className="flex justify-between font-mono text-xs">
                          <span className="text-primary">{i + 1}. {l.name}</span>
                          <span className="text-muted-foreground">{l.score} · LV{l.level}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            {hud.state === "paused" && (
              <>
                <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">{t("pilot.paused_label")}</p>
                <h2 className="font-display font-black text-3xl tracking-tighter">{t("pilot.paused_title")}</h2>
                <button onClick={pause} data-testid="pilot-resume" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-xs px-5 py-3">
                  <Play className="inline h-4 w-4 mr-1" /> {t("pilot.resume")}
                </button>
                <button onClick={() => setHud((h) => ({ ...h, state: "menu" }))} className="w-full rounded-sm border border-primary/30 hover:bg-primary/10 font-mono tracking-widest text-xs px-5 py-2 text-primary">
                  {t("pilot.menu")}
                </button>
              </>
            )}
            {hud.state === "gameover" && (
              <>
                <p className="font-mono text-[10px] tracking-[0.3em] text-red-400">{t("pilot.gameover_label")}</p>
                <h2 className="font-display font-black text-4xl tracking-tighter">{t("pilot.gameover_title")}</h2>
                <div className="grid grid-cols-2 gap-3 font-mono text-xs">
                  <div className="rounded-sm border border-primary/20 bg-black/50 p-3">
                    <p className="text-[10px] tracking-widest text-muted-foreground">{t("pilot.hud_score")}</p>
                    <p className="mt-1 font-display font-black text-2xl text-primary">{hud.score}</p>
                  </div>
                  <div className="rounded-sm border border-primary/20 bg-black/50 p-3">
                    <p className="text-[10px] tracking-widest text-muted-foreground">{t("pilot.hud_level")}</p>
                    <p className="mt-1 font-display font-black text-2xl text-secondary">{hud.level}</p>
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-muted-foreground">{t("pilot.save_score_as")}</label>
                  {passportLocked ? (
                    <div className="mt-2 w-full rounded-sm border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 font-mono text-sm text-emerald-300 flex items-center gap-2" data-testid="pilot-save-name-locked">
                      <span className="text-emerald-400">👽</span>
                      <span className="tracking-widest">{pilotName}</span>
                    </div>
                  ) : (
                    <input
                      value={pilotName}
                      onChange={(e) => setPilotName(e.target.value.slice(0, 14))}
                      placeholder={t("pilot.pilot_name_placeholder")}
                      data-testid="pilot-save-input"
                      className="mt-2 w-full rounded-sm border border-primary/30 bg-black/60 px-3 py-2 font-mono text-sm text-foreground focus:outline-none focus:border-primary"
                    />
                  )}
                </div>
                <div className="flex gap-3">
                  <button onClick={submitScore} data-testid="pilot-save" className="flex-1 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-xs px-4 py-3">{t("pilot.save")}</button>
                  <button onClick={startGame} data-testid="pilot-retry" className="flex-1 rounded-sm border border-secondary/50 hover:bg-secondary/10 text-secondary font-mono tracking-widest text-xs px-4 py-3">
                    <RotateCcw className="inline h-3.5 w-3.5 mr-1" /> {t("pilot.retry")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ShopModal open={shopOpen} onClose={() => setShopOpen(false)} onSkinChange={handleSkinChange} />
      <ShareRankCard open={shareOpen} onClose={() => setShareOpen(false)} stats={myStats} equippedSkin={currentSkin} />
      <TournamentResultModal user={FEATURES.rooms ? user : null} />
      <RequiredPassportModal user={user} onCreated={() => fetchMyStats()} />
      <MobileControls gameRef={gameRef} active={hud.state === "playing"} />
    </div>
  );
}
